import {
  Wind,
  Waves,
  Eye,
  Gauge,
  Droplets,
  Thermometer,
  Sunrise,
  Sunset,
  Moon,
  Fish,
  ShieldCheck,
  AlertTriangle,
  RotateCw,
  CloudRain,
  Sun,
  Compass,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { useConditions } from '@/hooks/useConditions';
import {
  degToCompass,
  moonPhasePt,
  safetyLevel,
  cptecTempoLabel,
  classifyWaterBody,
  waterBodyLabel,
  type DayForecast,
} from '@/lib/marine';
import { describeWeatherCode } from '@/lib/weather';
import { cn } from '@/lib/utils';

export function ConditionsPage() {
  const { data, fetchedAt, loading, error, lat, lon, refresh } = useConditions();

  const w = data?.weather ?? null;
  const m = data?.marine ?? null;
  const s = data?.solunar ?? null;
  const b = data?.br ?? null;
  const body = classifyWaterBody(data?.place ?? null, m);
  const isSea = body === 'mar';
  const safety = safetyLevel(w, m, body);

  const onRefresh = () => {
    if (lat != null && lon != null) refresh(lat, lon, data?.place ?? null, true);
  };

  return (
    <div className="space-y-5 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold">Condições</h1>
            <span
              className={cn(
                'rounded-full px-2 py-0.5 text-[10px] font-bold',
                isSea ? 'bg-brand/10 text-brand' : 'bg-status-running/10 text-status-running',
              )}
            >
              {waterBodyLabel(body)}
            </span>
          </div>
          <p className="text-sm text-muted-foreground">
            {data?.place
              ? `Referência: ${data.place}`
              : lat != null
                ? `Referência: ${lat.toFixed(3)}, ${lon?.toFixed(3)}`
                : 'Aguardando localização da frota…'}
          </p>
        </div>
        <button
          type="button"
          onClick={onRefresh}
          disabled={loading}
          className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2 text-sm font-semibold hover:bg-accent disabled:opacity-50"
        >
          <RotateCw className={cn('h-4 w-4', loading && 'animate-spin')} />
          {loading ? 'Atualizando…' : 'Atualizar'}
        </button>
      </div>

      {error && (
        <p className="rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
          Não foi possível carregar as condições ({error}). As fontes são públicas e podem
          oscilar — tente novamente.
        </p>
      )}

      {!data && loading && (
        <p className="text-sm text-muted-foreground">Carregando condições…</p>
      )}

      {/* ===== 1. SEGURANÇA DE NAVEGAÇÃO ===== */}
      <section className="space-y-2">
        <SectionTitle icon={ShieldCheck} title="Segurança de navegação" />
        <Card>
          <CardContent className="space-y-4 p-4">
            <div
              className={cn(
                'flex items-start gap-3 rounded-xl px-4 py-3',
                safety.level === 'ok' && 'bg-status-running/10 text-status-running',
                safety.level === 'atencao' && 'bg-status-ready/10 text-status-ready',
                safety.level === 'critico' && 'bg-status-blocked/10 text-status-blocked',
              )}
            >
              {safety.level === 'ok' ? (
                <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0" />
              ) : (
                <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
              )}
              <div>
                <p className="font-bold">
                  {safety.level === 'ok'
                    ? 'Favorável'
                    : safety.level === 'atencao'
                      ? 'Atenção'
                      : 'Condições severas'}
                </p>
                <ul className="text-xs">
                  {safety.reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Metric icon={Wind} label="Vento" value={fmt(w?.windKn, ' nós')} sub={dirLabel(w?.windDirDeg)} />
              <Metric icon={Wind} label="Rajadas" value={fmt(w?.gustKn, ' nós')} />
              {isSea ? (
                <Metric icon={Waves} label="Ondas" value={fmt(m?.waveHeight, ' m', 1)} sub={dirLabel(m?.waveDirDeg)} />
              ) : (
                <Metric icon={CloudRain} label="Chuva" value={fmt(w?.precipitation, ' mm')} />
              )}
              <Metric icon={Eye} label="Visibilidade" value={visLabel(w?.visibilityM)} />
            </div>
          </CardContent>
        </Card>
      </section>

      {/* ===== 2. PREVISÃO (mar) / TEMPO (águas interiores) ===== */}
      <section className="space-y-2">
        <SectionTitle icon={isSea ? Waves : CloudRain} title={isSea ? 'Previsão marítima' : 'Tempo nas águas'} />
        <Card>
          <CardContent className="space-y-4 p-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {isSea && <Metric icon={Waves} label="Altura de onda" value={fmt(m?.waveHeight, ' m', 1)} />}
              {isSea && <Metric icon={Compass} label="Direção da onda" value={dirLabel(m?.waveDirDeg) ?? '—'} />}
              {isSea && <Metric icon={RotateCw} label="Período" value={fmt(m?.wavePeriod, ' s')} />}
              {isSea && m?.seaTemp != null && <Metric icon={Thermometer} label="Temp. da água" value={fmt(m?.seaTemp, ' °C')} />}
              <Metric icon={Thermometer} label="Temp. ar" value={fmt(w?.temp, ' °C')} sub={w?.apparentTemp != null ? `sensação ${Math.round(w.apparentTemp)}°` : undefined} />
              <Metric icon={Wind} label="Vento" value={fmt(w?.windKn, ' nós')} sub={dirLabel(w?.windDirDeg)} />
              <Metric icon={Gauge} label="Pressão" value={fmt(w?.pressure, ' hPa')} />
              <Metric icon={Droplets} label="Umidade" value={fmt(w?.humidity, '%')} />
              <Metric icon={CloudRain} label="Chuva" value={fmt(w?.precipitation, ' mm')} />
            </div>
            {w?.weatherCode != null && (
              <p className="text-sm font-medium text-muted-foreground">
                Tempo agora: {describeWeatherCode(w.weatherCode)}
              </p>
            )}

            {data && data.days.length > 0 && (
              <div className="grid grid-cols-3 gap-2 pt-1">
                {data.days.map((d) => (
                  <DayCard key={d.date} day={d} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      {/* ===== FONTES BRASILEIRAS (CPTEC/INPE · INMET) ===== */}
      {b && (b.cptec || b.inmet) && (
        <section className="space-y-2">
          <SectionTitle icon={Waves} title="Fontes brasileiras (CPTEC/INPE · INMET)" />
          <Card>
            <CardContent className="space-y-4 p-4">
              {isSea && b.cptec && b.cptec.ondas.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold text-muted-foreground">
                    Ondas hoje — CPTEC/INPE ({b.cptec.cidade})
                  </p>
                  <div className="grid grid-cols-3 gap-2">
                    {b.cptec.ondas.map((o) => (
                      <div key={o.periodo} className="rounded-xl bg-secondary/60 p-3 text-center">
                        <p className="text-[11px] font-semibold capitalize">{o.periodo}</p>
                        <p className="mt-1 text-base font-bold">{fmt(o.altura, ' m', 1)}</p>
                        <p className="text-[11px] text-muted-foreground">{o.agitacao ?? '—'}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {o.direcao ?? '—'} · vento {fmt(o.vento, ' kn')} {o.vento_dir ?? ''}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {b.cptec && b.cptec.previsao.length > 0 && (
                <div>
                  <p className="mb-2 text-xs font-semibold text-muted-foreground">
                    Tempo — CPTEC/INPE
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {b.cptec.previsao.slice(0, 4).map((d) => (
                      <div key={d.dia} className="rounded-xl border border-border p-2 text-center">
                        <p className="text-[11px] font-semibold capitalize">{shortDay(d.dia)}</p>
                        <p className="text-[10px] text-muted-foreground">{cptecTempoLabel(d.tempo)}</p>
                        <p className="mt-1 text-xs font-bold">
                          {fmt(d.minima, '°')}/{fmt(d.maxima, '°')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {b.inmet && (
                <div className="rounded-xl bg-secondary/60 p-3">
                  <p className="text-xs font-semibold text-muted-foreground">
                    Estação INMET mais próxima
                  </p>
                  <p className="text-sm font-bold">
                    {b.inmet.estacao}{' '}
                    <span className="font-normal text-muted-foreground">
                      · {fmt(b.inmet.distancia_km, ' km', 1)}
                    </span>
                  </p>
                  {b.inmet.temp != null ? (
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                      <span>🌡 {fmt(b.inmet.temp, ' °C')}</span>
                      <span>💧 {fmt(b.inmet.umidade, '%')}</span>
                      <span>🔵 {fmt(b.inmet.pressao, ' hPa')}</span>
                      <span>💨 {fmt(msToKn(b.inmet.vento_vel_ms), ' kn')}</span>
                      <span>🌧 {fmt(b.inmet.chuva_mm, ' mm')}</span>
                    </div>
                  ) : (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Sem leitura recente publicada para esta estação.
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </section>
      )}

      {/* ===== 3. PESCA / SOLUNAR ===== */}
      <section className="space-y-2">
        <SectionTitle icon={Fish} title="Pesca / Solunar" />
        <Card>
          <CardContent className="space-y-4 p-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Metric icon={Sunrise} label="Nascer do sol" value={s?.sunRise ?? firstDaySun(data?.days, 'sunrise')} />
              <Metric icon={Sunset} label="Pôr do sol" value={s?.sunSet ?? firstDaySun(data?.days, 'sunset')} />
              <Metric icon={Moon} label="Lua" value={moonPhasePt(s?.moonPhase ?? null) ?? '—'} sub={s?.moonIllumination != null ? `${Math.round(s.moonIllumination * 100)}%` : undefined} />
              <Metric icon={Sun} label="UV máx." value={fmt(data?.days?.[0]?.uvMax, '')} />
            </div>

            {s && (s.major.length > 0 || s.minor.length > 0) ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <PeriodBox title="Períodos maiores" periods={s.major} tone="ok" hint="melhores horários" />
                <PeriodBox title="Períodos menores" periods={s.minor} tone="warn" hint="bons horários" />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Tábua solunar indisponível para esta posição agora.
              </p>
            )}
            {s?.dayRating != null && (
              <div className="flex items-center gap-2 text-sm">
                <span className="font-medium">Qualidade do dia p/ pesca:</span>
                <RatingDots value={s.dayRating} />
              </div>
            )}
          </CardContent>
        </Card>
      </section>

      <p className="pb-2 text-center text-[11px] text-muted-foreground">
        Fontes públicas (Open-Meteo, Solunar) — atualizado{' '}
        {fetchedAt ? new Date(fetchedAt).toLocaleString('pt-BR') : '—'}. Dados orientativos;
        confira fontes oficiais (Marinha/CHM) antes de navegar.
      </p>
    </div>
  );
}

// ---------- helpers de formatação ----------
function fmt(v: number | null | undefined, unit: string, decimals = 0): string {
  if (v == null) return '—';
  return `${v.toFixed(decimals).replace('.', ',')}${unit}`;
}
function dirLabel(deg: number | null | undefined): string | undefined {
  const c = degToCompass(deg);
  return c ? `${c} (${Math.round(deg!)}°)` : undefined;
}
function visLabel(m: number | null | undefined): string {
  if (m == null) return '—';
  return m >= 1000 ? `${(m / 1000).toFixed(0)} km` : `${Math.round(m)} m`;
}
function msToKn(ms: number | null | undefined): number | null {
  return ms == null ? null : ms * 1.94384;
}
function shortDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
}
function firstDaySun(days: DayForecast[] | undefined, key: 'sunrise' | 'sunset'): string {
  const v = days?.[0]?.[key];
  if (!v) return '—';
  // ISO "2026-06-27T06:14" → "06:14"
  const t = v.split('T')[1];
  return t ? t.slice(0, 5) : v;
}

// ---------- componentes ----------
function SectionTitle({ icon: Icon, title }: { icon: typeof Wind; title: string }) {
  return (
    <h2 className="flex items-center gap-2 px-1 text-sm font-bold">
      <Icon className="h-4 w-4 text-brand" />
      {title}
    </h2>
  );
}

function Metric({
  icon: Icon,
  label,
  value,
  sub,
}: {
  icon: typeof Wind;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="rounded-xl bg-secondary/60 p-3">
      <div className="flex items-center gap-1.5 text-[11px] font-medium text-muted-foreground">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <p className="mt-1 text-base font-bold leading-tight">{value}</p>
      {sub && <p className="text-[11px] text-muted-foreground">{sub}</p>}
    </div>
  );
}

function DayCard({ day }: { day: DayForecast }) {
  const d = new Date(`${day.date}T12:00:00`);
  const label = d.toLocaleDateString('pt-BR', { weekday: 'short' }).replace('.', '');
  return (
    <div className="rounded-xl border border-border p-2 text-center">
      <p className="text-[11px] font-semibold capitalize">{label}</p>
      <p className="text-[11px] text-muted-foreground">
        {fmt(day.tempMin, '°')}/{fmt(day.tempMax, '°')}
      </p>
      {day.waveMax != null ? (
        <>
          <p className="mt-1 text-xs font-bold">{fmt(day.waveMax, ' m', 1)}</p>
          <p className="text-[10px] text-muted-foreground">onda máx.</p>
        </>
      ) : (
        <p className="mt-1 text-xs font-bold">{fmt(day.windMaxKn, ' nós')}</p>
      )}
      <p className="text-[10px] text-muted-foreground">
        vento {fmt(day.windMaxKn, ' nós')}
      </p>
    </div>
  );
}

function PeriodBox({
  title,
  periods,
  tone,
  hint,
}: {
  title: string;
  periods: { start: string; stop: string }[];
  tone: 'ok' | 'warn';
  hint: string;
}) {
  return (
    <div
      className={cn(
        'rounded-xl p-3',
        tone === 'ok' ? 'bg-status-running/10' : 'bg-status-ready/10',
      )}
    >
      <p
        className={cn(
          'text-xs font-bold',
          tone === 'ok' ? 'text-status-running' : 'text-status-ready',
        )}
      >
        {title} <span className="font-normal opacity-70">· {hint}</span>
      </p>
      <ul className="mt-1 space-y-0.5 text-sm font-semibold">
        {periods.length > 0 ? (
          periods.map((p, i) => (
            <li key={i}>
              {p.start} – {p.stop}
            </li>
          ))
        ) : (
          <li className="font-normal text-muted-foreground">—</li>
        )}
      </ul>
    </div>
  );
}

function RatingDots({ value }: { value: number }) {
  return (
    <span className="inline-flex gap-1">
      {[0, 1, 2, 3].map((i) => (
        <span
          key={i}
          className={cn(
            'h-2.5 w-2.5 rounded-full',
            i < value ? 'bg-status-running' : 'bg-secondary',
          )}
        />
      ))}
    </span>
  );
}
